import type {
  NotificationData,
  NotificationDataWire,
  NotificationModel,
  NotificationModelWire,
} from '@/shared/types/notifications';
import { fromSubjectDisplay } from './subject-mapper';

/**
 * Normalize the PascalCase nested `Data.AppointmentId` field to
 * camelCase. Mirrors the dental-list precedent (PR #271 `fromDentalWire`).
 *
 * Per Samer's PR #259 review note: normalizer-style functions need direct
 * unit-test coverage including a drift-guard for extra wire fields.
 *
 * **Not idempotent by design** — same Flutter-verbatim brittleness as
 * `fromDentalWire` (PR #271). If the backend ever flips to camelCase
 * (`appointmentId`), both sides break symmetrically; that's a contract
 * change, not a defensive default. The magic-strings-verbatim rule
 * means we don't mask wire shifts.
 */
export function fromNotificationDataWire(
  wire: NotificationDataWire,
): NotificationData {
  return {
    appointmentId: wire.AppointmentId ?? '',
  };
}

/**
 * Normalize the full notification wire shape. Maps:
 *   - `Data.AppointmentId` → `data.appointmentId` (PascalCase drift)
 *   - `subject` display string → `subject` enum (e.g. "Appointment Booked" → APPOINTMENT_BOOKED)
 *   - `isSeen: null` → `isSeen: false` (Flutter ships nullable; consumers want a definite boolean)
 *
 * The original display string is preserved as `subjectDisplay` for UI
 * code that wants the human-readable form without re-deriving.
 */
export function fromNotificationWire(
  wire: NotificationModelWire,
): NotificationModel {
  return {
    id: wire.id,
    message: wire.message,
    subject: fromSubjectDisplay(wire.subject),
    subjectDisplay: wire.subject,
    createdOn: wire.createdOn,
    isRead: wire.isRead,
    isSeen: wire.isSeen ?? false,
    pushData: wire.pushData,
    data: wire.data ? fromNotificationDataWire(wire.data) : null,
  };
}
